import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ValueDialog({ result, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (path, body) => {
    setBusy(true);
    try { const r = await api.post(`/discovery/findings/${result.id}/${path}`, body); setData(r.data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const close = () => { setData(null); setReason(""); onClose(); };
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="value-dialog">
        <DialogHeader><DialogTitle>Safe preview — {result?.column}</DialogTitle>
          <DialogDescription>{result?.asset} · {result?.category_label}. Values are fetched live from the source and never stored.</DialogDescription></DialogHeader>
        {!data && <Button onClick={() => run("preview", { purpose: "Data preview" })} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="preview-masked-button"><ShieldCheck className="mr-1.5 h-4 w-4" />{busy ? "Fetching…" : "Show masked preview"}</Button>}
        {data && (
          <div className="space-y-2">
            <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${data.masked ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="mask-state">
              {data.masked ? <><EyeOff className="h-3 w-3" />Masked by default · {data.method_label} ({data.policy})</> : <><Eye className="h-3 w-3" />Original values — access audited</>}
            </div>
            <ul className="divide-y rounded-md border font-mono text-xs" data-testid="preview-values">{data.values.length ? data.values.map((v, i) => <li key={i} className="px-3 py-1.5">{v}</li>) : <li className="px-3 py-1.5 text-slate-400">No non-empty values in the current sample.</li>}</ul>
            <p className="text-[11px] text-slate-500">{data.note}</p>
          </div>
        )}
        {data?.masked && can(user, "reveal") && (
          <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/60 p-3">
            <div className="text-xs font-semibold text-amber-900">Reveal original values (explicit authorisation)</div>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Business reason (min 10 characters) — recorded in the audit log" data-testid="reveal-reason" />
            <Button size="sm" variant="outline" disabled={reason.trim().length < 10 || busy} onClick={() => run("reveal", { reason })} data-testid="reveal-button"><Eye className="mr-1 h-4 w-4" />Reveal</Button>
          </div>
        )}
        {data?.masked && !can(user, "reveal") && <p className="text-[11px] text-slate-500" data-testid="reveal-not-permitted">Only Privacy / DPO and Organisation Admins can reveal original values.</p>}
      </DialogContent>
    </Dialog>
  );
}
